import {
  deleteConversationMessage,
  deleteUserConversationMessage,
  deleteUserGroupMessage,
  getConversation,
  getConversationMessages,
  getUserConversation,
  getUserConversationMessages,
  getUserGroupMessages,
  leaveConversation,
  leaveUserGroup,
  markConversationRead,
  markUserConversationRead,
  markUserGroupRead,
  muteConversation,
  muteUserGroup,
  presignConversationAttachment,
  presignUserConversationAttachment,
  presignUserGroupAttachment,
  sendConversationMessage,
  sendUserConversationMessage,
  sendUserGroupMessage,
  translateConversationMessage,
  translateUserConversationMessage,
  translateUserGroupMessage,
  updateConversationMessage,
  updateUserConversationMessage,
  updateUserGroupMessage,
  USER_CONVERSATION_ID,
} from '../../api/conversations.api';
import { isAdmin, isDeliveries } from '../../helpers/appType';
import {
  ConversationAttachmentPresignResponse,
  ConversationMessageEntity,
  GetConversationMessagesParams,
  MuteConversationDto,
  PresignConversationAttachmentDto,
  SendConversationMessageDto,
} from '../../types/conversation.types';
import {
  deliveriesDeleteMessage,
  deliveriesFetchMessages,
  deliveriesPatchMessage,
  deliveriesPostMessage,
  deliveriesPresignAttachment,
  deliveriesReadConversation,
} from './deliveriesChatTransport';

/**
 * One thread, three route families.
 *
 * The admin API addresses a conversation by id. The customer API has no ids
 * for the support thread, because an outside user has exactly one with vGrubs
 * - and then does have them for the merchant groups that user is in, under
 * `/v2`. Everything else about a thread - paging, optimistic sends,
 * attachments, read receipts - is identical, so the whole `ChatThread` is
 * shared and only the URL differs.
 *
 * That difference is resolved here, once, rather than as a branch in each of
 * the hooks that make a request. Callers pass a `conversationId` because it is
 * the cache key either way; on the merchant side the support thread's is the
 * `USER_CONVERSATION_ID` sentinel and is simply not used in the URL, while a
 * group's is its real id and is.
 */

/** Which family a merchant-side id belongs to. */
const isSupportThread = (conversationId: number | string) =>
  conversationId === USER_CONVERSATION_ID;

export const fetchConversationMessages = (
  conversationId: number | string,
  params: GetConversationMessagesParams
) => {
  // The deliveries app talks to a localStorage prototype, not the network -
  // same early-return shape the dev branch uses for the 'ai' sentinel.
  if (isDeliveries) return deliveriesFetchMessages(conversationId, params);
  return isAdmin
    ? getConversationMessages(conversationId, params)
    : isSupportThread(conversationId)
    ? getUserConversationMessages(params)
    : getUserGroupMessages(conversationId, params);
};

export const postConversationMessage = (
  conversationId: number | string,
  data: SendConversationMessageDto
) => {
  if (isDeliveries) return deliveriesPostMessage(conversationId, data);
  return isAdmin
    ? sendConversationMessage(conversationId, data)
    : isSupportThread(conversationId)
    ? sendUserConversationMessage(data)
    : sendUserGroupMessage(conversationId, data);
};

export const patchConversationMessage = (
  conversationId: number | string,
  messageId: number,
  body: string
) => {
  if (isDeliveries)
    return deliveriesPatchMessage(conversationId, messageId, body);
  return isAdmin
    ? updateConversationMessage(conversationId, messageId, body)
    : isSupportThread(conversationId)
    ? updateUserConversationMessage(messageId, body)
    : updateUserGroupMessage(conversationId, messageId, body);
};

export const removeConversationMessage = (
  conversationId: number | string,
  messageId: number
) => {
  if (isDeliveries) return deliveriesDeleteMessage(conversationId, messageId);
  return isAdmin
    ? deleteConversationMessage(conversationId, messageId)
    : isSupportThread(conversationId)
    ? deleteUserConversationMessage(messageId)
    : deleteUserGroupMessage(conversationId, messageId);
};

/** Only the reader sees the result - nothing is stored on the message. */
export const translateMessage = (
  conversationId: number | string,
  messageId: number
) =>
  isAdmin
    ? translateConversationMessage(conversationId, messageId)
    : isSupportThread(conversationId)
    ? translateUserConversationMessage(messageId)
    : translateUserGroupMessage(conversationId, messageId);

export const presignAttachment = (
  conversationId: number | string,
  data: PresignConversationAttachmentDto
) => {
  if (isDeliveries) return deliveriesPresignAttachment(conversationId, data);
  return isAdmin
    ? presignConversationAttachment(conversationId, data)
    : isSupportThread(conversationId)
    ? presignUserConversationAttachment(data)
    : presignUserGroupAttachment(conversationId, data);
};

/**
 * Marks the thread read. Every variant returns the caller's fresh unread
 * counts, but in its app's shape - admin `{ threads, messages, ... }`, merchant
 * `{ unread, support, groups, ... }` - so the result is handed back
 * untyped-ish and `useChatRead` writes whichever one it got straight into the
 * badge cache. Recomputing a count client side drifts the moment a second tab
 * or a `read-all` is involved.
 */
export const readConversation = (conversationId: number | string) => {
  if (isDeliveries) return deliveriesReadConversation(conversationId);
  return isAdmin
    ? markConversationRead(conversationId)
    : isSupportThread(conversationId)
    ? markUserConversationRead()
    : markUserGroupRead(conversationId);
};

/**
 * A group's detail - members, title, image, mute. Never called for the
 * merchant's support thread, which has no detail route at all.
 */
export const fetchConversationDetail = (conversationId: number | string) =>
  isAdmin
    ? getConversation(conversationId)
    : getUserConversation(conversationId);

export const setConversationMute = (
  conversationId: number | string,
  data: MuteConversationDto
) =>
  isAdmin
    ? muteConversation(conversationId, data)
    : muteUserGroup(conversationId, data);

export const leaveGroupConversation = (conversationId: number | string) =>
  isAdmin ? leaveConversation(conversationId) : leaveUserGroup(conversationId);

/** Narrow helper so the message types above stay honest at call sites. */
export type ConversationMessageResult = { data: ConversationMessageEntity };
export type ConversationPresignResult = {
  data: ConversationAttachmentPresignResponse;
};
